// #region Imports

/* Telestream */
import {WAVEFORM_ELEMENTS, WAVEFORM_MARKS_COLORS} from '@/utils/constants';
import {searchForCue} from "@user-interface/glim-player/lib/services/metadata.service";

// #endregion Imports

// #region Constants

const WAVEFORM_BARS_COLOR         = '#0099ee';
const WAVEFORM_HEIGHT_PERCENTAGE  = 0.7;

const NIELSEN_FONT                = '12px sans-serif';
const NIELSEN_TEXT_ALIGN          = 'center';
const NIELSEN_FONT_FILL_STYLE     = 'white';

const MARKERS_STROKE_COLOR        = 'black';

const FLAG_INSIDE_CIRCLE_COLOR    = '#1E1E1E';
const FLAG_TEXT_Y                 = 9;
export const FLAG_Y               = 21;
export const FLAG_RADIUS          = 8;

// #endregion Constants

/**
 * Clear the context to get it ready to draw the graph.
 *
 * @param context The context to which the graph will be drawn.
 */
export function clearCanvas(context) {
  context.clearRect(0, 0, context.canvas.width, context.canvas.height);
}

export function renderWaveform(context, cues, start, end) {
  context.save();
  context.translate(0, context.canvas.height / 2);
  context.fillStyle = WAVEFORM_BARS_COLOR;
  context.beginPath();

  const waveformHeight = (context.canvas.height / 2) * WAVEFORM_HEIGHT_PERCENTAGE;
  const offsetMultiplier = (context.canvas.width / (end - start));

  cues.forEach(cue => {
    let w = (((cue.endTime - cue.startTime) / (end - start)) * context.canvas.width) - 1;
    context.rect(
      (cue.startTime - start) * offsetMultiplier,
      cue.value.meter.minMin * waveformHeight,
      w,
      (cue.value.meter.maxMax - cue.value.meter.minMin) * waveformHeight
    );
  });

  context.fill();
  context.restore();
}

export function renderNielsenFlags(context, cues, start, end, metadataCues) {
  cues.forEach(cue => {
    if (cue.value.meter.nielsen) {
      //align it with metadata flags
      let metadataCue = searchForCue(metadataCues, cue.startTime);
      if (!metadataCue) return;
      let startTime = metadataCue.startTime;
      let endTime = metadataCue.endTime;
      const offsetMultiplier = (context.canvas.width / (end - start));
      let positionInTheMiddleOfTheFrame = (startTime - start) + (endTime - startTime) / 2;
      let x = positionInTheMiddleOfTheFrame * offsetMultiplier;
      drawFlag(context, start, end, cue, WAVEFORM_MARKS_COLORS.NIELSEN, x);
      context.resetTransform();
      context.font = NIELSEN_FONT;
      context.textAlign = NIELSEN_TEXT_ALIGN;
      context.fillStyle = NIELSEN_FONT_FILL_STYLE;
      context.lineWidth = 3;
      context.strokeStyle = MARKERS_STROKE_COLOR;
      context.strokeText(cue.value.meter.nielsen[1].value, x, FLAG_TEXT_Y);
      context.fillText(cue.value.meter.nielsen[1].value, x, FLAG_TEXT_Y);
    }
  });
}

function drawIPBFrames(cue, frameMetadataMaxLength, context, selectedWaveformMarkers, start, end) {
  let percentageOfLargestFrame = cue.value.info.sampleLength / frameMetadataMaxLength * 0.2;
  if (percentageOfLargestFrame < 0.020) percentageOfLargestFrame = 0.015 + percentageOfLargestFrame / 4;
  let pictureMarkHeight = percentageOfLargestFrame * context.canvas.height;
  if (cue.value.info.sampleType === '0' && selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.GOP)) {
    // drawMark(context, markXPosition, pictureMarkHeight, WAVEFORM_MARKS_COLORS.I_FRAME, width);
    drawMark(context, start, end, cue, WAVEFORM_MARKS_COLORS.I_FRAME, pictureMarkHeight);
  } else if (cue.value.info.sampleType === '1' && selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.GOP)) {
    // drawMark(context, markXPosition, pictureMarkHeight, WAVEFORM_MARKS_COLORS.P_FRAME, width);
    drawMark(context, start, end, cue, WAVEFORM_MARKS_COLORS.P_FRAME, pictureMarkHeight);
  } else if (
    cue.value.info.sampleType !== '0' && cue.value.info.sampleType !== '1' && selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.GOP)
  ) {
    // drawMark(context, markXPosition, pictureMarkHeight, WAVEFORM_MARKS_COLORS.B_FRAME, width);
    drawMark(context, start, end, cue, WAVEFORM_MARKS_COLORS.B_FRAME, pictureMarkHeight);
  }
}

export function renderMetadata(
  context,
  cues,
  start,
  end,
  frameMetadataMaxLength,
  selectedWaveformMarkers,
  hideMetadata
) {
  for (const cue of cues) {
    const offsetMultiplier = (context.canvas.width / (end - start));
    let positionInTheMiddleOfTheFrame = (cue.startTime - start) + (cue.endTime - cue.startTime) / 2;
    let x = positionInTheMiddleOfTheFrame * offsetMultiplier;
    if (cue.value.info.hasScte35 && selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.SCTE_35)) {
      drawFlag(context, start, end, cue, WAVEFORM_MARKS_COLORS.SCTE_35, x)
    }
    if (cue.value.info.hasScte104 && selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.SCTE_104)) {
      drawFlag(context, start, end, cue, WAVEFORM_MARKS_COLORS.SCTE_104, x)
    }
    if (selectedWaveformMarkers.includes(WAVEFORM_ELEMENTS.GOP) && !hideMetadata){
      drawIPBFrames(cue, frameMetadataMaxLength, context, selectedWaveformMarkers, start, end);
    }
  }
}

function drawFlag(context, start, end, cue, color,x){
  context.translate(0, 0);
  context.fillStyle = color;
  context.strokeStyle = color;
  context.lineWidth = 2;
  context.beginPath();

  let w = 8;
  let y = 20
  // x is in the middle of the cue to place flag properly
  context.arc(x, y, w, 0, 2 * Math.PI);
  context.fill();
  context.stroke()
  context.lineWidth = 1;
  context.beginPath()
  context.moveTo(x-w, y+3.5)
  context.lineTo(x +w, y+3.5);
  context.lineTo(x, 1.85*y);
  context.fill()
  context.stroke()
  context.fillStyle = FLAG_INSIDE_CIRCLE_COLOR;
  context.strokeStyle = FLAG_INSIDE_CIRCLE_COLOR;
  context.lineWidth = 2;
  context.beginPath();
  context.arc(x, y, w/4, 0, 2 * Math.PI);
  context.fill();
  context.stroke()
}

function drawMark(context, start, end, cue, color, pictureMarkHeight){
  context.save();
  context.translate(0, context.canvas.height - pictureMarkHeight);
  context.fillStyle = color;
  context.beginPath();

  const offsetMultiplier = (context.canvas.width / (end - start));

  let w = (((cue.endTime - cue.startTime) / (end - start)) * context.canvas.width) - 1;
  context.rect(
    (cue.startTime - start) * offsetMultiplier,
    0,
    w,
    pictureMarkHeight
  );

  context.fill();
  context.restore();
}

